import React, { useContext, useEffect, useState } from 'react';
import { Modal, View, Text, TextInput, TouchableOpacity, FlatList, StyleSheet } from 'react-native';
import { SystemContext } from '../helpers/SystemContext';
import { priceFormatter } from "../helpers";
import { useTranslation } from 'react-i18next';
import { ModalKeyboardAvoider } from './KeyboardAware';
import { foodCategoryIds } from '../helpers/foodCategories';

const ExtrasModal = ({ visible, item, onClose, onSave }) => {
    const { t } = useTranslation();
    const { foods = [] } = useContext(SystemContext) || {};
    const [selectedExtras, setSelectedExtras] = useState([]);
    const [filteredFoods, setFilteredFoods] = useState([]);

    useEffect(() => {
        if (item?.extras) {
            // Multi-category foods match when ANY of their categories is in
            // item.extras (the whitelist); visible/enabled gates unchanged.
            const matchingFoods = foods.filter(food => foodCategoryIds(food).some((id) => item.extras.includes(id)) && food.visible === true && food.enabled === true);
            setFilteredFoods(matchingFoods);

            // Reset selected extras when modal is opened with a new item
            setSelectedExtras([]);

            console.log('Filtered foods for extras:', matchingFoods);
        }
    }, [item, foods]);

    const toggleExtra = (extraItem) => {
        const extra = {
            item: extraItem,
            price: extraItem.price || extraItem?.sizes[0]?.price
        };

        // Check if this item is already selected
        const isAlreadySelected = selectedExtras.some(
            (extra) => extra.item.id === extraItem.id
        );

        if (isAlreadySelected) {
            // Remove this item from selection
            setSelectedExtras(
                selectedExtras.filter((extra) => extra.item.id !== extraItem.id)
            );
        } else {
            // Add this item to selection
            setSelectedExtras([...selectedExtras, extra]);
        }

        // Log for debugging
        console.log('Selected extras after toggle:', 
            isAlreadySelected 
                ? selectedExtras.filter((extra) => extra.item.id !== extraItem.id).map(e => e.item.id)
                : [...selectedExtras, extra].map(e => e.item.id)
        );
    };

    return (
        <Modal visible={visible} animationType="slide" transparent>
            <ModalKeyboardAvoider style={styles.overlay} justify="center">
                <View style={styles.container}>
                    <TouchableOpacity style={styles.closeButton} onPress={()=> {
                        onClose(false)
                        setSelectedExtras([]);
                    }}>
                        <Text style={styles.closeText}>{t('close')}</Text>
                    </TouchableOpacity>
                    <Text style={styles.title}>{t('extras_modal_title')}</Text>
                    <FlatList
                        style={styles.list}
                        keyboardShouldPersistTaps="handled"
                        data={filteredFoods}
                        keyExtractor={(item) => item.id}
                        extraData={selectedExtras} // Add extraData to ensure FlatList re-renders when selectedExtras changes
                        renderItem={({ item }) => {
                            // Explicitly check if this item is selected
                            const isSelected = selectedExtras.some(
                                (extra) => extra.item.id === item.id
                            );

                            return (
                                <TouchableOpacity
                                    style={[
                                        styles.extraItem,
                                        isSelected && styles.selectedExtra
                                    ]}
                                    onPress={() => toggleExtra(item)}
                                >
                                    <Text 
                                        style={[
                                            styles.extraText,
                                            isSelected && styles.selectedText
                                        ]}
                                    >
                                        {item?.name?.hu || item?.name?.en || item?.name}
                                    </Text>
                                    <Text 
                                        style={[
                                            styles.price,
                                            isSelected && styles.selectedText
                                        ]}
                                    >
                                        {priceFormatter(item.price || item?.sizes[0]?.price)}
                                    </Text>
                                </TouchableOpacity>
                            );
                        }}
                    />
                    <TouchableOpacity style={styles.saveButton} onPress={() => {
                        onSave({...item, extras: selectedExtras})
                        setSelectedExtras([]);
                    }}>
                        <Text style={styles.saveText}>{t('save')}</Text>
                    </TouchableOpacity>
                </View>
            </ModalKeyboardAvoider>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    container: {
        backgroundColor: 'white',
        width: '90%',
        maxHeight: '90%',
        padding: 20,
        borderRadius: 10,
    },
    closeButton: {
        backgroundColor: 'red',
        padding: 10,
        alignItems: 'center',
        borderRadius: 5,
        marginBottom: 10,
    },
    closeText: {
        color: 'white',
        fontWeight: 'bold',
    },
    title: {
        fontSize: 18,
        fontWeight: 'bold',
        marginBottom: 10,
    },
    input: {
        borderWidth: 1,
        borderColor: '#ccc',
        padding: 10,
        marginBottom: 10,
        borderRadius: 5,
    },
    // Scrolls inside the card so Save stays visible with many extras.
    list: {
        flexGrow: 0,
        flexShrink: 1,
    },
    extraItem: {
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 44,
        padding: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#ddd',
    },
    selectedExtra: {
        backgroundColor: '#FFDDC1',
        borderWidth: 1,
        borderColor: '#FF8C00',
    },
    extraText: {
        flex: 1,
    },
    selectedText: {
        color: '#FF8C00',
        fontWeight: 'bold',
    },
    price: {
        fontWeight: 'bold',
    },
    saveButton: {
        backgroundColor: 'orange',
        padding: 10,
        alignItems: 'center',
        borderRadius: 5,
        marginTop: 10,
    },
    saveText: {
        color: 'white',
        fontWeight: 'bold',
    },
});

export default ExtrasModal;
